import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Download, Pencil, ArrowRightLeft, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import api, { formatApiError } from "@/lib/api";
import { DOC_TYPES } from "@/lib/constants";
import { useApp } from "@/context/AppContext";
import { PageHeader, StatusBadge, ConfirmDelete } from "@/components/common";
import PrintableDocument from "@/components/PrintableDocument";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

async function exportPdf(el, filename) {
  const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
  const pdf = new jsPDF({ unit: "pt", format: "letter" });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const ih = (canvas.height * pw) / canvas.width;
  const img = canvas.toDataURL("image/jpeg", 0.95);
  let y = 0;
  while (y < ih - 1) {
    if (y > 0) pdf.addPage();
    pdf.addImage(img, "JPEG", 0, -y, pw, ih);
    y += ph;
  }
  pdf.save(filename);
}

export default function DocumentView({ type }) {
  const cfg = DOC_TYPES[type];
  const { id } = useParams();
  const nav = useNavigate();
  const { settings } = useApp();
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();

  useEffect(() => {
    api.get(`${cfg.endpoint}/${id}`).then((r) => setDoc(r.data)).catch(() => nav(cfg.base));
  }, [id, cfg, nav]);

  if (!doc || !settings) return <p className="text-stone-500">Cargando…</p>;

  const download = async () => {
    setBusy(true);
    try {
      await exportPdf(ref.current, `${doc.folio}-${doc.client_name.replace(/\s+/g, "_")}.pdf`);
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (status) => {
    const { data } = await api.patch(`${cfg.endpoint}/${id}/status`, { status });
    setDoc(data);
    toast.success("Estado actualizado");
  };

  const convert = async () => {
    try {
      const { data } = await api.post(`/quotes/${id}/convert`);
      toast.success(`Nota de venta ${data.folio} creada`);
      nav(`/ventas/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    await api.delete(`${cfg.endpoint}/${id}`);
    toast.success("Documento eliminado");
    nav(cfg.base);
  };

  return (
    <div>
      <Link to={cfg.base} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-amber-700 mb-3" data-testid="back-to-list">
        <ArrowLeft className="h-4 w-4" />{cfg.title}
      </Link>
      <PageHeader title={`${cfg.single} ${doc.folio}`} subtitle={doc.quote_folio ? `Generada desde cotización ${doc.quote_folio}` : doc.client_name}>
        <Select value={doc.status} onValueChange={changeStatus}>
          <SelectTrigger className="w-40 bg-white" data-testid="view-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{cfg.statuses.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        {type === "quote" && !doc.converted_sale_id && (
          <Button variant="outline" onClick={convert} data-testid="convert-to-sale-button"><ArrowRightLeft className="h-4 w-4 mr-1" />Convertir a nota de venta</Button>
        )}
        {doc.converted_sale_id && (
          <Button variant="outline" asChild data-testid="open-converted-sale"><Link to={`/ventas/${doc.converted_sale_id}`}>Ver nota de venta</Link></Button>
        )}
        <Button variant="outline" asChild data-testid="edit-document-button"><Link to={`${cfg.base}/${id}/editar`}><Pencil className="h-4 w-4 mr-1" />Editar</Link></Button>
        <Button onClick={download} disabled={busy} className="bg-amber-700 hover:bg-amber-800" data-testid="download-pdf-button">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Descargar PDF
        </Button>
        <ConfirmDelete onConfirm={remove} testId="delete-document-button" label={`la ${cfg.single.toLowerCase()} ${doc.folio}`} />
      </PageHeader>
      <div className="mb-3"><StatusBadge status={doc.status} testId="document-status-badge" /></div>
      <div className="overflow-x-auto rounded-xl border bg-stone-200/60 p-4 sm:p-8">
        <div className="mx-auto w-fit shadow-xl">
          <PrintableDocument ref={ref} doc={doc} settings={settings} title={cfg.pdfTitle} />
        </div>
      </div>
    </div>
  );
}
